import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw, Plug, CheckCircle2, AlertTriangle, Database, Zap,
  Link2, Unlink, DollarSign, MousePointerClick, Eye, Target
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };
const today = () => new Date().toISOString().split("T")[0];
const fmt = (n) => (n || 0).toLocaleString();
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function GoogleAdsApiPage() {
  const { sessionToken } = useAuth();
  const tenantId = sessionStorage.getItem("viewing_tenant_id");

  const call = useCallback((method, path, extraParams = {}) => {
    return axios({
      method,
      url: `${API}/google-ads-api/${path}`,
      params: { tenant_id: tenantId, Authorization: `Bearer ${sessionToken}`, ...extraParams },
    });
  }, [tenantId, sessionToken]);

  const [status, setStatus] = useState(null);
  const [mcc, setMcc] = useState("727-017-6297");
  const [accounts, setAccounts] = useState([]);
  const [selectedCid, setSelectedCid] = useState("");
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await call("get", "status");
      setStatus(res.data);
      if (res.data.manager_customer_id) setMcc(res.data.manager_customer_id);
      if (res.data.selected_customer_id) setSelectedCid(res.data.selected_customer_id);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [call, tenantId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Handle OAuth redirect result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1") {
      toast.success("Google Ads connected successfully");
      window.history.replaceState({}, "", window.location.pathname);
      fetchStatus();
    } else if (params.get("error")) {
      toast.error(`Connection failed: ${params.get("error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await call("get", "oauth/start", { manager_customer_id: mcc });
      window.location.assign(res.data.auth_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await call("post", "disconnect");
      toast.success("Disconnected");
      setAccounts([]); setSelectedCid(""); setData(null);
      fetchStatus();
    } catch (e) { toast.error("Failed to disconnect"); }
  };

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await call("get", "accounts");
      setAccounts(res.data.accounts || []);
      toast.success(`Found ${res.data.accounts?.length || 0} accounts`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load accounts");
    }
    setLoadingAccounts(false);
  };

  const runSync = async () => {
    if (!selectedCid) { toast.error("Select an account first"); return; }
    setSyncing(true);
    try {
      const res = await call("post", "sync", { customer_id: selectedCid, start_date: startDate, end_date: endDate });
      toast.success(`Synced ${res.data.campaigns_synced} campaign rows, ${res.data.keywords_synced} keyword rows`);
      await loadData();
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  const loadData = async () => {
    try {
      const res = await call("get", "data", {
        start_date: startDate, end_date: endDate,
        ...(selectedCid ? { customer_id: selectedCid } : {}),
      });
      setData(res.data);
    } catch (e) { toast.error("Failed to load data"); }
  };

  // Build daily chart series from campaigns
  const chartData = React.useMemo(() => {
    if (!data?.campaigns) return [];
    const byDate = {};
    data.campaigns.forEach((c) => {
      if (!byDate[c.date]) byDate[c.date] = { date: c.date, cost: 0, clicks: 0 };
      byDate[c.date].cost += c.cost || 0;
      byDate[c.date].clicks += c.clicks || 0;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-slate-400" data-testid="gads-api-loading">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading…
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="google-ads-api-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">Google Ads API</h1>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">Live · Beta</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Pull campaign & keyword data live from your Google Ads Manager account.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status?.connected && (
            <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50" data-testid="gads-api-connected-badge">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Connected
            </Badge>
          )}
        </div>
      </div>

      {/* Non-disruption notice */}
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3 text-xs text-blue-800" data-testid="gads-api-notice">
        <Database className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          This is a separate, additive data source. Your existing <strong>Google Ads (CSV upload)</strong> page and all
          uploaded data are completely untouched — keep using your monthly uploads as normal.
        </span>
      </div>

      {/* Connection */}
      <Card data-testid="gads-api-connection-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Plug className="w-4 h-4 text-slate-600" />Connection</CardTitle>
        </CardHeader>
        <CardContent>
          {!status?.developer_token_configured && (
            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">
              <AlertTriangle className="w-4 h-4" />Developer token not configured on the server.
            </div>
          )}
          {status?.connected ? (
            <div className="flex flex-wrap items-center gap-4">
              <div className="text-sm">
                <div className="text-slate-500 text-xs">Manager account (MCC)</div>
                <div className="font-mono font-medium">{status.manager_customer_id}</div>
              </div>
              <div className="text-sm">
                <div className="text-slate-500 text-xs">Connected</div>
                <div className="font-medium">{status.connected_at ? new Date(status.connected_at).toLocaleDateString() : "—"}</div>
              </div>
              <div className="text-sm">
                <div className="text-slate-500 text-xs">Last sync</div>
                <div className="font-medium">{status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : "Never"}</div>
              </div>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={connect} disabled={connecting} data-testid="gads-api-reconnect-btn">
                  <Link2 className="w-4 h-4 mr-1" />Reconnect
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={disconnect} data-testid="gads-api-disconnect-btn">
                  <Unlink className="w-4 h-4 mr-1" />Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Manager account ID (MCC)</label>
                <Input value={mcc} onChange={(e) => setMcc(e.target.value)} className="w-48 font-mono" placeholder="727-017-6297" data-testid="gads-api-mcc-input" />
              </div>
              <Button onClick={connect} disabled={connecting} className="bg-blue-600 hover:bg-blue-700" data-testid="gads-api-connect-btn">
                {connecting ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Plug className="w-4 h-4 mr-1" />}
                Connect Google Ads
              </Button>
              <span className="text-xs text-slate-400">You'll be sent to Google to authorize read access.</span>
            </div>
          )}
        </CardContent>
      </Card>

      {status?.connected && (
        <>
          {/* Accounts + Sync controls */}
          <Card data-testid="gads-api-accounts-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-slate-600" />Accounts & sync</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" onClick={loadAccounts} disabled={loadingAccounts} data-testid="gads-api-load-accounts-btn">
                  {loadingAccounts ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Load accounts under MCC
                </Button>
                {accounts.length > 0 && <span className="text-xs text-slate-400">{accounts.length} accounts</span>}
              </div>

              {accounts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="gads-api-accounts-list">
                  {accounts.map((a) => (
                    <button
                      key={a.customer_id}
                      onClick={() => setSelectedCid(a.customer_id)}
                      className={`text-left p-3 rounded-lg border transition-colors ${selectedCid === a.customer_id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`gads-api-account-${a.customer_id}`}
                    >
                      <div className="font-medium text-sm text-slate-800 truncate">{a.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{a.customer_id}</div>
                      {a.is_manager && <Badge variant="secondary" className="text-[10px] mt-1">Manager</Badge>}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Account ID</label>
                  <Input value={selectedCid} onChange={(e) => setSelectedCid(e.target.value)} className="w-40 font-mono" placeholder="10-digit ID" data-testid="gads-api-cid-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Start</label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" data-testid="gads-api-start-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">End</label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" data-testid="gads-api-end-date" />
                </div>
                <Button onClick={runSync} disabled={syncing || !selectedCid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="gads-api-sync-btn">
                  {syncing ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                  Sync now
                </Button>
                <Button variant="outline" onClick={loadData} data-testid="gads-api-load-data-btn">View synced data</Button>
              </div>
            </CardContent>
          </Card>

          {/* Data */}
          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gads-api-kpis">
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" />Impressions</div>
                  <div className="text-2xl font-bold">{fmt(data.totals?.impressions)}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><MousePointerClick className="w-3 h-3" />Clicks</div>
                  <div className="text-2xl font-bold">{fmt(data.totals?.clicks)}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" />Spend</div>
                  <div className="text-2xl font-bold">{money(data.totals?.cost)}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Target className="w-3 h-3" />Conversions</div>
                  <div className="text-2xl font-bold">{fmt(data.totals?.conversions)}</div>
                </CardContent></Card>
              </div>

              {chartData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Daily spend & clicks</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar yAxisId="l" dataKey="cost" name="Spend ($)" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                        <Bar yAxisId="r" dataKey="clicks" name="Clicks" fill="#10B981" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Campaigns ({data.campaign_count})</CardTitle></CardHeader>
                <CardContent>
                  {data.campaigns?.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="gads-api-campaigns-table">
                        <thead>
                          <tr className="border-b text-left text-xs text-slate-500 uppercase">
                            <th className="pb-2 pr-3">Campaign</th><th className="pb-2 pr-3">Date</th>
                            <th className="pb-2 pr-3 text-right">Impr.</th><th className="pb-2 pr-3 text-right">Clicks</th>
                            <th className="pb-2 pr-3 text-right">Cost</th><th className="pb-2 pr-3 text-right">Conv.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.campaigns.slice(0, 200).map((c, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="py-2 pr-3 font-medium text-slate-800">{c.campaign_name}</td>
                              <td className="py-2 pr-3 text-slate-400">{c.date}</td>
                              <td className="py-2 pr-3 text-right">{fmt(c.impressions)}</td>
                              <td className="py-2 pr-3 text-right">{fmt(c.clicks)}</td>
                              <td className="py-2 pr-3 text-right">{money(c.cost)}</td>
                              <td className="py-2 pr-3 text-right">{c.conversions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-sm text-slate-400 py-4">No campaign data for this range yet. Run a sync above.</p>}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
